import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  RefreshCw,
  Image as ImageIcon,
  User,
  MessageSquare,
  ZoomIn,
  Loader2
} from 'lucide-react';
import { usePaginatedMediaData, ProfilePhoto, ChatMedia } from '@/hooks/usePaginatedMediaData';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { AdminImageZoom } from './AdminImageZoom';
import AdminPhotoImage from './AdminPhotoImage';
import LazyImageWithSpinner from './LazyImageWithSpinner';
import { debugLogger } from '@/utils/debugLogger';
import { useAdminFilter } from '@/contexts/AdminFilterContext';
import { useImageUrl } from '@/hooks/useImageUrl';

interface RealMediaViewerProps {
  mediaType: 'profile' | 'chat';
}

// Sub-component for profile photos with signed URL generation
const ProfilePhotoCardWrapper: React.FC<{ photo: ProfilePhoto; displayName: string }> = ({ photo, displayName }) => {
  const { url: signedUrl, isLoading } = useImageUrl(photo.url, 'thumbnail');
  
  if (isLoading) {
    return (
      <div className="aspect-square bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
        <Loader2 className="h-5 w-5 text-luxury-pinkBright animate-spin" />
      </div>
    );
  }

  if (!signedUrl) {
    return (
      <div className="aspect-square bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
        <ImageIcon className="h-6 w-6 text-white/40" />
      </div>
    );
  }

  return (
    <AdminImageZoom
      src={signedUrl}
      alt={`profile - ${displayName}`}
      file={{
        name: `profile-${photo.id}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        username: photo.username
      }}
      userProfiles={{}}
      onDownload={() => {
        const link = document.createElement('a');
        link.href = signedUrl;
        link.download = `profile-${photo.id}`;
        link.click();
      }}
      onDelete={() => {
        debugLogger.debug(`Delete requested for profile ${photo.id}`);
      }}
      isDeleting={false}
    >
      <div className="group relative aspect-square rounded-lg overflow-hidden bg-white/5 border border-white/10 hover:border-luxury-pinkBright/50 transition-all duration-200 cursor-pointer">
        <img
          src={signedUrl}
          alt={`profile - ${displayName}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <ZoomIn className="h-6 w-6 text-white" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="flex items-center gap-1 text-xs text-white">
            <User className="h-3 w-3" />
            <span className="truncate">{displayName}</span>
          </div>
          <div className="text-xs text-luxury-pinkBright">
            {photo.type === 'main' ? 'Principal' : 'Galeria'}
          </div>
        </div>
      </div>
    </AdminImageZoom>
  );
};

const RealMediaViewer: React.FC<RealMediaViewerProps> = ({ mediaType }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { userTypeFilter } = useAdminFilter();
  
  const { 
    items, 
    loading, 
    hasMore, 
    loadMore, 
    refetch, 
    totalCount 
  } = usePaginatedMediaData({
    mediaType,
    userTypeFilter,
    pageSize: 10
  });

  const { observerTarget } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    isLoading: loading,
    threshold: 300
  });
  // Filtrar localmente apenas nos itens já carregados
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return items;
    
    const term = searchTerm.toLowerCase();
    
    if (mediaType === 'profile') {
      return items.filter(item => {
        const photo = item as ProfilePhoto;
        return photo.username?.toLowerCase().includes(term) ||
               photo.url.toLowerCase().includes(term) ||
               photo.type.toLowerCase().includes(term);
      });
    } else {
      return items.filter(item => {
        const media = item as ChatMedia;
        return media.url.toLowerCase().includes(term) ||
               media.conversationId.toLowerCase().includes(term) ||
               media.senderUsername?.toLowerCase().includes(term);
      });
    }
  }, [items, searchTerm, mediaType]);

  const getDisplayName = (item: ProfilePhoto | ChatMedia): string => {
    if (mediaType === 'profile') {
      const photo = item as ProfilePhoto;
      if (photo.username) return photo.username;
      if (photo.userId) return photo.userId.substring(0, 8) + '...';
      return 'Usuário desconhecido';
    } else {
      const media = item as ChatMedia;
      if (media.senderUsername) return media.senderUsername;
      if (media.conversationId) return media.conversationId.substring(0, 8) + '...';
      if (media.senderId) return media.senderId.substring(0, 8) + '...';
      return 'Remetente desconhecido';
    }
  };

  if (loading && filteredData.length === 0) {
    return (
      <div className="space-y-4">
        <div className="h-10 bg-white/10 rounded animate-pulse"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-square bg-white/10 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-4">
      {/* Search and Stats */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              placeholder={`Buscar ${mediaType === 'profile' ? 'fotos de perfil' : 'mídia de chat'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-sm text-white/60">
              {searchTerm 
                ? `${filteredData.length} encontrados` 
                : `${items.length} carregados de ${totalCount} totais`
              }
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={refetch}
              disabled={loading}
              className="text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Media Grid */}
      {filteredData.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
          {filteredData.map((item) => {
            const displayName = getDisplayName(item);
            
            if (mediaType === 'chat') {
              return (
                <div key={item.id} className="relative group">
                  <LazyImageWithSpinner
                    originalUrl={item.url}
                    alt={`${mediaType} - ${displayName}`}
                    className="aspect-square"
                  />
                  
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="flex items-center gap-1 text-xs text-white">
                      <MessageSquare className="h-3 w-3" />
                      <span className="truncate">{displayName}</span>
                    </div>
                    <div className="text-xs text-white/60">
                      {new Date((item as ChatMedia).createdAt).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                </div>
              );
            }
            
            // Para profile photos - usar useImageUrl para signed URL gerada sob demanda
            const photo = item as ProfilePhoto;
            
            return (
              <ProfilePhotoCardWrapper
                key={item.id}
                photo={photo}
                displayName={displayName}
              />
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <ImageIcon className="h-12 w-12 text-white/40 mx-auto mb-4" />
          <p className="text-white/60">
            {searchTerm 
              ? `Nenhuma ${mediaType === 'profile' ? 'foto de perfil' : 'mídia de chat'} encontrada para "${searchTerm}"` 
              : `Nenhuma ${mediaType === 'profile' ? 'foto de perfil' : 'mídia de chat'} encontrada`
            }
          </p>
        </div>
      )}

      {/* Infinite Scroll Trigger */}
      {!searchTerm && (
        <div ref={observerTarget} className="h-20 flex items-center justify-center">
          {loading && (
            <div className="flex items-center gap-2 text-white/60">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Carregando mais...</span>
            </div>
          )}
          {!loading && hasMore && filteredData.length > 0 && (
            <p className="text-white/40 text-sm">Role para carregar mais...</p>
          )}
          {!hasMore && items.length > 0 && (
            <p className="text-white/40 text-sm">Todas as mídias carregadas ({items.length})</p>
          )}
        </div>
      )}
    </div>
  );
};

export default RealMediaViewer;
